import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Star, Check, ExternalLink, Shield, Clock, Award, Zap, Trophy, Gamepad2, Headphones, Globe, Smartphone, Bitcoin, CreditCard } from 'lucide-react';

const BITSTARZ_LOGO = 'https://customer-assets.emergentagent.com/job_smart-player-hub/artifacts/cnni6myb_logo-light.png';
const BITSTARZ_LINK = 'https://bzstarz1.com/bhi8kvogt';

const FEATURE_ICONS = { zap: Zap, trophy: Trophy, gamepad: Gamepad2, shield: Shield, headphones: Headphones, globe: Globe };

const DEPOSIT_BREAKDOWN = [
  { label: 'First Deposit', detail: '100% up to $100 or 1 BTC + 180 Free Spins' },
  { label: 'Second Deposit', detail: '50% match up to $100 or 1 BTC' },
  { label: 'Third Deposit', detail: '50% match up to $200 or 2 BTC' },
  { label: 'Fourth Deposit', detail: '100% match up to $100 or 1 BTC' },
];

const TOP_RTP_GAMES = [
  { name: 'Mega Joker', provider: 'NetEnt', rtp: 99.0 },
  { name: 'Blood Suckers', provider: 'NetEnt', rtp: 98.0 },
  { name: 'White Rabbit', provider: 'Big Time Gaming', rtp: 97.72 },
  { name: 'Book of Dead', provider: "Play'n GO", rtp: 96.21 },
  { name: 'Starburst', provider: 'NetEnt', rtp: 96.09 },
  { name: "Gonzo's Quest", provider: 'NetEnt', rtp: 95.97 },
];

const PAYMENT_METHODS = ['Bitcoin', 'Ethereum', 'USDT', 'Litecoin', 'Visa', 'Mastercard', 'Skrill', 'Neteller'];

export default function CasinoPartners() {
  const [casino, setCasino] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCasino = async () => {
      try {
        const res = await api.get('/casinos');
        if (res.data.casinos?.length > 0) setCasino(res.data.casinos[0]);
      } catch {
        toast.error('Failed to load casino data');
      } finally {
        setLoading(false);
      }
    };
    fetchCasino();
  }, []);

  const affiliateUrl = `${BITSTARZ_LINK}?utm_source=casinoiq&utm_medium=casino-partners&utm_campaign=smart-player-tools`;

  const handleVisit = async () => {
    try { await api.post('/affiliate/click', { casino_id: 'bitstarz', source_page: 'casino-partners' }); } catch {}
    window.open(affiliateUrl, '_blank', 'noopener');
  };

  if (loading) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <Skeleton className="h-10 w-64 bg-[#2d3548] mb-4" />
        <Skeleton className="h-[400px] w-full bg-[#2d3548] rounded-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="casino-partners-page">
      <div className="mb-8 animate-fade-in">
        <h1 className="text-2xl lg:text-[32px] font-bold text-white" style={{ letterSpacing: '-0.005em' }}>Featured Casino Partner</h1>
        <p className="text-[#a0a8b8] text-sm mt-2">Our verified partner with the best bonuses and highest RTP games</p>
      </div>

      {/* Hero Card */}
      <div className="ciq-card border-[#4d7cfe]/20 mb-8 animate-fade-in">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: Logo + Rating */}
          <div className="lg:w-[320px] shrink-0">
            <div className="w-full h-24 rounded-xl bg-black flex items-center justify-center p-4 mb-4">
              <img src={BITSTARZ_LOGO} alt="BitStarz Casino" className="h-full object-contain" data-testid="bitstarz-hero-logo" />
            </div>
            <div className="flex items-center gap-2 mb-2">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-4 w-4 ${i < 4 ? 'text-[#f59e0b] fill-[#f59e0b]' : 'text-[#f59e0b]/40 fill-[#f59e0b]/40'}`} />
                ))}
              </div>
              <span className="text-sm text-[#a0a8b8]">4.3/5 TrustPilot</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="badge-green text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1"><Shield className="h-3 w-3" /> Licensed</span>
              <span className="badge-blue text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1"><Bitcoin className="h-3 w-3" /> Crypto</span>
              <span className="badge-purple text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1"><Smartphone className="h-3 w-3" /> Mobile</span>
            </div>
            <div className="mt-4 space-y-2 text-xs text-[#64748b]">
              <div className="flex justify-between"><span>Established</span><span className="text-white">2014</span></div>
              <div className="flex justify-between"><span>License</span><span className="text-white">Curacao eGaming</span></div>
              <div className="flex justify-between"><span>Withdrawal</span><span className="text-[#10b981]">10-30 min (crypto)</span></div>
              <div className="flex justify-between"><span>Games</span><span className="text-white">7,000+</span></div>
              <div className="flex justify-between"><span>Avg RTP</span><span className="text-[#10b981]">96.8%</span></div>
            </div>
          </div>

          {/* Right: Bonus + Features */}
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-white mb-1" data-testid="bitstarz-name">BitStarz Casino</h2>
            <p className="text-[#a0a8b8] text-sm mb-5">Award-winning crypto casino with 7,000+ games and lightning-fast withdrawals</p>

            {/* Bonus Banner */}
            <div className="p-5 rounded-xl bg-gradient-to-r from-[#4d7cfe]/10 to-[#8b5cf6]/10 border border-[#4d7cfe]/20 mb-6">
              <p className="text-xs text-[#a0a8b8] uppercase tracking-wider mb-1 font-medium">Welcome Bonus</p>
              <p className="text-2xl font-bold text-white mb-1" data-testid="bitstarz-bonus">$500 or 5 BTC + 180 Free Spins</p>
              <p className="text-xs text-[#64748b]">40x wagering | Min deposit $20 | Free spins on Book of Pyramids, Wolf Treasure</p>
            </div>

            {/* Deposit Breakdown */}
            <div className="mb-6">
              <h3 className="text-white font-semibold text-sm mb-3">Bonus Breakdown</h3>
              <div className="space-y-2">
                {DEPOSIT_BREAKDOWN.map((d, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <span className="w-6 h-6 rounded-full bg-[#4d7cfe]/15 text-[#4d7cfe] text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <span className="text-[#a0a8b8]"><span className="text-white font-medium">{d.label}:</span> {d.detail}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={handleVisit}
                className="h-12 px-8 rounded-xl bg-[#4d7cfe] text-white font-medium text-sm btn-lift flex items-center justify-center gap-2"
                data-testid="bitstarz-visit-btn">
                Visit BitStarz <ExternalLink className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Grid */}
      <h2 className="text-xl font-semibold text-white mb-4">Why BitStarz?</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {[
          { icon: Zap, color: '#f59e0b', title: 'Lightning-Fast Withdrawals', desc: 'Crypto payouts in minutes, not days' },
          { icon: Trophy, color: '#f59e0b', title: 'Award-Winning Casino', desc: 'Best Online Casino 2022, 2023, 2024' },
          { icon: Gamepad2, color: '#4d7cfe', title: '7,000+ Premium Games', desc: 'Slots, table games, live dealer' },
          { icon: Shield, color: '#10b981', title: 'Provably Fair Gaming', desc: 'Blockchain-verified game outcomes' },
          { icon: Headphones, color: '#8b5cf6', title: '24/7 Live Support', desc: 'Instant help via chat, email, phone' },
          { icon: Globe, color: '#4d7cfe', title: 'Crypto-Friendly', desc: 'Bitcoin, Ethereum, Litecoin, USDT' },
        ].map((f, i) => (
          <div key={i} className="ciq-card py-4" data-testid={`bitstarz-feature-${i}`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${f.color}15` }}>
                <f.icon className="h-4 w-4" style={{ color: f.color }} />
              </div>
              <div>
                <h4 className="text-white font-medium text-sm">{f.title}</h4>
                <p className="text-[#64748b] text-xs">{f.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Top RTP Games at BitStarz */}
      <h2 className="text-xl font-semibold text-white mb-4">Top RTP Games at BitStarz</h2>
      <div className="ciq-card mb-8">
        <div className="space-y-2">
          {TOP_RTP_GAMES.map((g, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0" data-testid={`bitstarz-rtp-game-${i}`}>
              <div>
                <span className="text-white text-sm font-medium">{g.name}</span>
                <span className="text-[#64748b] text-xs ml-2">{g.provider}</span>
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded-md ${g.rtp >= 98 ? 'bg-[#10b981]/15 text-[#10b981]' : g.rtp >= 96 ? 'bg-[#f59e0b]/15 text-[#f59e0b]' : 'bg-[#a0a8b8]/15 text-[#a0a8b8]'}`}>
                {g.rtp}% RTP
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Methods */}
      <h2 className="text-xl font-semibold text-white mb-4">Payment Methods</h2>
      <div className="ciq-card mb-8">
        <div className="flex flex-wrap gap-2">
          {PAYMENT_METHODS.map((m, i) => (
            <span key={i} className="px-3 py-1.5 rounded-lg bg-[#1a1f2e] border border-white/10 text-sm text-[#a0a8b8]" data-testid={`payment-method-${i}`}>
              {['Bitcoin', 'Ethereum', 'USDT', 'Litecoin'].includes(m) && <Bitcoin className="h-3 w-3 inline mr-1.5 text-[#f59e0b]" />}
              {['Visa', 'Mastercard'].includes(m) && <CreditCard className="h-3 w-3 inline mr-1.5 text-[#4d7cfe]" />}
              {m}
            </span>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div className="p-3 rounded-lg bg-[#1a1f2e]">
            <p className="text-[#64748b] text-xs">Crypto Withdrawal</p>
            <p className="text-[#10b981] font-medium">10-30 minutes</p>
          </div>
          <div className="p-3 rounded-lg bg-[#1a1f2e]">
            <p className="text-[#64748b] text-xs">Card Withdrawal</p>
            <p className="text-white font-medium">1-3 business days</p>
          </div>
          <div className="p-3 rounded-lg bg-[#1a1f2e]">
            <p className="text-[#64748b] text-xs">Minimum Deposit</p>
            <p className="text-white font-medium">$20 or 0.0005 BTC</p>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="ciq-card border-[#4d7cfe]/20 text-center py-8">
        <h2 className="text-xl font-bold text-white mb-2">Ready to Play at BitStarz?</h2>
        <p className="text-[#a0a8b8] text-sm mb-5 max-w-lg mx-auto">Join 7,000+ games with crypto-fast withdrawals and a welcome package worth up to $500 or 5 BTC + 180 Free Spins.</p>
        <button onClick={handleVisit}
          className="h-12 px-10 rounded-xl bg-[#4d7cfe] text-white font-medium btn-lift inline-flex items-center gap-2"
          data-testid="bitstarz-bottom-cta-btn">
          Visit BitStarz Casino <ExternalLink className="h-4 w-4" />
        </button>
        <p className="text-[#64748b] text-[10px] mt-3">18+ | T&Cs Apply | Gamble Responsibly | Curacao eGaming Licensed</p>
      </div>
    </div>
  );
}
